"use client";

import Image from "next/image";
import type { CSSProperties, ReactNode } from "react";
import { cn } from "../../lib";
import {
	GLASS_BORDER_DEFAULT_BORDER_WIDTH_PX,
	GLASS_SHELL_BORDER_WIDTH_PX,
	GlassBorder,
	GlassBorderPanel,
	type GlassBorderProps,
	type GlassBorderRadius,
	type GlassBorderTone,
} from "./glass-border";
import {
	PULSE_FLOAT_DEFAULT_DURATION_S,
	PulseFloatMotion,
} from "./pulse-float-motion";
import {
	resolveSpeechBubbleStackedArrowGeometry,
	type SpeechBubbleStackedArrowGeometry,
} from "./speech-bubble-stacked-arrow";
import {
	SPEECH_BUBBLE_SYSTEM_ARROW_BORDER_FILL,
	SPEECH_BUBBLE_SYSTEM_ARROW_CSS_VARS_CLASS,
	SPEECH_BUBBLE_SYSTEM_ARROW_FACE_FILL,
	SPEECH_BUBBLE_SYSTEM_ARROW_SHELL_FILL,
	SPEECH_BUBBLE_SYSTEM_FACE_BORDER_CLASS,
	SPEECH_BUBBLE_SYSTEM_GLASS_PLATE_CLASS,
	SPEECH_BUBBLE_SYSTEM_SHELL_BORDER_CLASS,
} from "./speech-bubble-system-chrome";

export type SpeechBubbleArrowDirection = "up" | "down";
export type SpeechBubbleVariant = "light" | "dark";

/** Default portrait bubble face size (px). */
export const SPEECH_BUBBLE_PORTRAIT_SIZE_PX = 72;
/** Inner image area inside the portrait bubble at default face size (px). */
export const SPEECH_BUBBLE_PORTRAIT_IMAGE_PX = 52;

export function resolvePortraitImagePx(faceSizePx: number): number {
	return Math.max(
		24,
		Math.round(
			faceSizePx *
				(SPEECH_BUBBLE_PORTRAIT_IMAGE_PX /
					SPEECH_BUBBLE_PORTRAIT_SIZE_PX),
		),
	);
}

interface SpeechBubbleSurfaceTokens {
	faceClassName: string;
	shellClassName: string | null;
	arrowShellUpClassName: string | null;
	arrowShellDownClassName: string | null;
	arrowUpOuterClassName: string;
	arrowUpInnerClassName: string;
	arrowDownOuterClassName: string;
	arrowDownInnerClassName: string;
}

/** Legacy solid shell padding (non-glass portrait path). */
const SPEECH_BUBBLE_SHELL_PADDING_PX = 3;
const SPEECH_BUBBLE_ARROW_SHELL_EXTRA_WIDTH_PX = 4;
const SPEECH_BUBBLE_ARROW_SHELL_HALF_WIDTH_PX =
	10 +
	SPEECH_BUBBLE_SHELL_PADDING_PX +
	SPEECH_BUBBLE_ARROW_SHELL_EXTRA_WIDTH_PX;
const SPEECH_BUBBLE_ARROW_SHELL_EXTRA_HEIGHT_PX = 5;
const SPEECH_BUBBLE_ARROW_SHELL_HEIGHT_PX =
	13 +
	SPEECH_BUBBLE_SHELL_PADDING_PX +
	SPEECH_BUBBLE_ARROW_SHELL_EXTRA_HEIGHT_PX;
const SPEECH_BUBBLE_ARROW_OUTER_HALF_WIDTH_PX = 10;
const SPEECH_BUBBLE_ARROW_OUTER_HEIGHT_PX = 13;
const SPEECH_BUBBLE_ARROW_INNER_HALF_WIDTH_PX = 9;
const SPEECH_BUBBLE_ARROW_INNER_HEIGHT_PX = 11;
const SPEECH_BUBBLE_ARROW_STACK_UPSHIFT_PX = 3;
const SPEECH_BUBBLE_ARROW_BORDER_INNER_EXTRA_DOWN_PX = 2;
const SPEECH_BUBBLE_ARROW_OVERLAY_EXTRA_DOWN_PX = 2;

type SpeechBubbleArrowMount = "edge" | "radial";

const SPEECH_BUBBLE_LIGHT_BORDER_CLASS =
	"border-[#D9D9D9] dark:border-[#D9D9D9]";
const SPEECH_BUBBLE_LIGHT_ARROW_BORDER_UP_CLASS =
	"border-b-[#D9D9D9] dark:border-b-[#D9D9D9]";
const SPEECH_BUBBLE_LIGHT_ARROW_BORDER_DOWN_CLASS =
	"border-t-[#D9D9D9] dark:border-t-[#D9D9D9]";
const SPEECH_BUBBLE_LIGHT_SHELL_OPAQUE_CLASS = "bg-[#D9D9D9]";
const SPEECH_BUBBLE_LIGHT_ARROW_SHELL_UP_CLASS = "border-b-[#D9D9D9]";
const SPEECH_BUBBLE_LIGHT_ARROW_SHELL_DOWN_CLASS = "border-t-[#D9D9D9]";

/**
 * @deprecated Prefer {@link SPEECH_BUBBLE_SYSTEM_ARROW_SHELL_FILL} for glass chrome.
 * Kept for legacy solid-shell / non-system call sites.
 */
export const SPEECH_BUBBLE_GLASS_ARROW_SHELL_FILL =
	SPEECH_BUBBLE_SYSTEM_ARROW_SHELL_FILL;

const SPEECH_BUBBLE_FLOAT_DROP_SHADOW =
	"drop-shadow-[0_3px_6px_rgba(0,0,0,0.2)] drop-shadow-[0_12px_28px_rgba(0,0,0,0.26)]";

export type SpeechBubbleGlassBorderProps = Omit<
	GlassBorderProps,
	"children" | "variant"
>;

const SPEECH_BUBBLE_VARIANT_TOKENS: Record<
	SpeechBubbleVariant,
	SpeechBubbleSurfaceTokens
> = {
	light: {
		faceClassName: cn(
			"rounded-full border bg-white",
			SPEECH_BUBBLE_LIGHT_BORDER_CLASS,
		),
		shellClassName: cn(
			"rounded-full p-[3px]",
			SPEECH_BUBBLE_LIGHT_SHELL_OPAQUE_CLASS,
		),
		arrowShellUpClassName: SPEECH_BUBBLE_LIGHT_ARROW_SHELL_UP_CLASS,
		arrowShellDownClassName: SPEECH_BUBBLE_LIGHT_ARROW_SHELL_DOWN_CLASS,
		arrowUpOuterClassName: SPEECH_BUBBLE_LIGHT_ARROW_BORDER_UP_CLASS,
		arrowUpInnerClassName: "border-b-white",
		arrowDownOuterClassName: SPEECH_BUBBLE_LIGHT_ARROW_BORDER_DOWN_CLASS,
		arrowDownInnerClassName: "border-t-white",
	},
	dark: {
		faceClassName:
			"rounded-2xl border border-[rgb(55_59_64)] bg-[#0f1320] text-white backdrop-blur-sm opacity-95",
		shellClassName: null,
		arrowShellUpClassName: null,
		arrowShellDownClassName: null,
		arrowUpOuterClassName: "border-b-[rgb(55_59_64)]",
		arrowUpInnerClassName: "border-b-[#0f1320]",
		arrowDownOuterClassName: "border-t-[rgb(55_59_64)]",
		arrowDownInnerClassName: "border-t-[#0f1320]",
	},
};

export type SpeechBubbleFaceShape = "circle" | "rounded-3xl";
export type SpeechBubbleDensity = "default" | "compact";

export interface SpeechBubbleProps {
	children?: ReactNode;
	imageSrc?: string;
	imageAlt?: string;
	arrowDirection?: SpeechBubbleArrowDirection;
	/** Rotates the arrow around the bubble center (degrees). Overrides `arrowDirection` when set. */
	arrowRotationDeg?: number;
	/** Optional CSS `transition` for the radial arrow (e.g. smooth rotation). */
	arrowRotationTransition?: string;
	variant?: SpeechBubbleVariant;
	faceShape?: SpeechBubbleFaceShape;
	/** Text-only bubbles: tighter padding and no portrait min size when `compact`. */
	density?: SpeechBubbleDensity;
	/**
	 * BlockQuote-authoritative frosted rim + split arrow layers.
	 * Defaults on for light text bubbles (no portrait); off for map portraits / radial arrows.
	 */
	glassBorder?: boolean;
	glassBorderWidth?: number;
	glassTone?: GlassBorderTone;
	/**
	 * Passed through to {@link GlassBorder} (tone, radius, shellBorderClassName, …).
	 * Defaults match PA system quote chrome when unset.
	 */
	glassBorderProps?: SpeechBubbleGlassBorderProps;
	/**
	 * Stacked arrow fills — same contract as BlockQuote.
	 * Defaults to {@link SPEECH_BUBBLE_SYSTEM_ARROW_*} CSS variables.
	 */
	arrowFill?: string;
	arrowBorderFill?: string;
	arrowShellFill?: string;
	/** Scales outermost arrow triangle; mid/fill step down by fixed rings (BlockQuote). */
	arrowScale?: number;
	/** Narration / non-anchored bubbles — omit pointer arrow entirely. */
	hideArrow?: boolean;
	floatDurationSec?: number;
	/** Idle float phase offset in seconds (stable per entity id). */
	floatDelaySec?: number;
	className?: string;
	/** Portrait bubble width/height (px). Ignored when `children` is text-only. */
	sizePx?: number;
	/** When false, skips the idle float animation wrapper. */
	enableFloat?: boolean;
}

function resolveSpeechBubbleArrowExtraDownPx(
	faceShape: SpeechBubbleFaceShape,
): number {
	return faceShape === "circle"
		? 0
		: SPEECH_BUBBLE_ARROW_OVERLAY_EXTRA_DOWN_PX;
}

function resolveSpeechBubbleFaceCenterTopPx(
	sizePx: number,
	hasShell: boolean,
): number {
	return (hasShell ? SPEECH_BUBBLE_SHELL_PADDING_PX : 0) + sizePx / 2;
}

function resolveSpeechBubbleArrowOffsetFromFaceCenterPx(
	sizePx: number,
	faceShape: SpeechBubbleFaceShape,
	hasShell: boolean,
): number {
	const shellPadding = hasShell ? SPEECH_BUBBLE_SHELL_PADDING_PX : 0;
	const extraDownPx = resolveSpeechBubbleArrowExtraDownPx(faceShape);
	return (
		sizePx / 2 +
		shellPadding +
		extraDownPx -
		SPEECH_BUBBLE_ARROW_STACK_UPSHIFT_PX -
		SPEECH_BUBBLE_ARROW_BORDER_INNER_EXTRA_DOWN_PX
	);
}

function resolveSpeechBubbleFaceRadiusClass(
	faceShape: SpeechBubbleFaceShape,
): string {
	return faceShape === "rounded-3xl" ? "rounded-3xl" : "rounded-full";
}

function resolveGlassRadius(
	faceShape: SpeechBubbleFaceShape,
): GlassBorderRadius {
	return faceShape === "rounded-3xl" ? "3xl" : "full";
}

function resolveSpeechBubbleTokens(
	variant: SpeechBubbleVariant,
	faceShape: SpeechBubbleFaceShape,
): SpeechBubbleSurfaceTokens {
	const base = SPEECH_BUBBLE_VARIANT_TOKENS[variant];
	if (faceShape === "circle") {
		return base;
	}
	const radiusClass = resolveSpeechBubbleFaceRadiusClass(faceShape);
	return {
		...base,
		faceClassName: base.faceClassName.replace(
			/\brounded-\S+/g,
			radiusClass,
		),
		shellClassName:
			base.shellClassName?.replace(/\brounded-\S+/g, radiusClass) ?? null,
	};
}

/** Tip offset for nesting a glass up-arrow under content above the bubble. */
export function resolveSpeechBubbleArrowUpTipOffsetPx(params?: {
	glassBorder?: boolean;
	glassBorderWidthPx?: number;
	arrowScale?: number;
}): number {
	const geometry = resolveSpeechBubbleStackedArrowGeometry({
		glassBorder: params?.glassBorder ?? true,
		glassBorderWidthPx: params?.glassBorderWidthPx,
		arrowScale: params?.arrowScale,
	});
	return geometry.shellHeightPx - geometry.stackUpshiftPx;
}

/** @deprecated Prefer {@link resolveSpeechBubbleArrowUpTipOffsetPx} with glass defaults. */
export const SPEECH_BUBBLE_ARROW_UP_TIP_OFFSET_PX =
	SPEECH_BUBBLE_ARROW_SHELL_HEIGHT_PX -
	SPEECH_BUBBLE_ARROW_STACK_UPSHIFT_PX +
	SPEECH_BUBBLE_ARROW_OVERLAY_EXTRA_DOWN_PX;

function SpeechBubbleShellArrow({
	direction,
	tokens,
	extraDownPx = 0,
	mount = "edge",
}: {
	direction: SpeechBubbleArrowDirection;
	tokens: SpeechBubbleSurfaceTokens;
	extraDownPx?: number;
	mount?: SpeechBubbleArrowMount;
}) {
	const shellHalfWidth = SPEECH_BUBBLE_ARROW_SHELL_HALF_WIDTH_PX;
	const shellHeight = SPEECH_BUBBLE_ARROW_SHELL_HEIGHT_PX;
	const stackUpshift = SPEECH_BUBBLE_ARROW_STACK_UPSHIFT_PX;
	const shellClassName =
		direction === "up"
			? tokens.arrowShellUpClassName
			: tokens.arrowShellDownClassName;
	if (!shellClassName) {
		return null;
	}

	const stackContainerStyle = {
		width: shellHalfWidth * 2,
		height: shellHeight,
		transform: `translateY(${extraDownPx - stackUpshift}px)`,
	} as const;

	if (direction === "up") {
		if (mount === "radial") {
			return null;
		}
		return (
			<span
				className="pointer-events-none absolute left-1/2 top-0 z-0 flex -translate-x-1/2 -translate-y-full justify-center leading-none"
				aria-hidden
			>
				<span className="relative shrink-0" style={stackContainerStyle}>
					<span
						className={cn(
							"absolute left-1/2 bottom-0 z-0 size-0 -translate-x-1/2 border-x-transparent",
							shellClassName,
						)}
						style={{
							borderLeftWidth: shellHalfWidth,
							borderRightWidth: shellHalfWidth,
							borderBottomWidth: shellHeight,
						}}
					/>
				</span>
			</span>
		);
	}

	const shellTriangleDown = (
		<span className="relative shrink-0" style={stackContainerStyle}>
			<span
				className={cn(
					"absolute left-1/2 top-0 z-0 size-0 -translate-x-1/2 border-x-transparent",
					shellClassName,
				)}
				style={{
					borderLeftWidth: shellHalfWidth,
					borderRightWidth: shellHalfWidth,
					borderTopWidth: shellHeight,
				}}
			/>
		</span>
	);

	if (mount === "radial") {
		return (
			<span
				className="pointer-events-none flex justify-center leading-none"
				aria-hidden
			>
				{shellTriangleDown}
			</span>
		);
	}

	return (
		<span
			className="pointer-events-none absolute bottom-0 left-1/2 z-0 flex -translate-x-1/2 translate-y-[calc(100%-5px)] justify-center leading-none"
			aria-hidden
		>
			{shellTriangleDown}
		</span>
	);
}

function SpeechBubbleArrow({
	direction,
	tokens,
	extraDownPx = 0,
	mount = "edge",
}: {
	direction: SpeechBubbleArrowDirection;
	tokens: SpeechBubbleSurfaceTokens;
	extraDownPx?: number;
	mount?: SpeechBubbleArrowMount;
}) {
	const outerHalfWidth = SPEECH_BUBBLE_ARROW_OUTER_HALF_WIDTH_PX;
	const outerHeight = SPEECH_BUBBLE_ARROW_OUTER_HEIGHT_PX;
	const innerHalfWidth = SPEECH_BUBBLE_ARROW_INNER_HALF_WIDTH_PX;
	const innerHeight = SPEECH_BUBBLE_ARROW_INNER_HEIGHT_PX;
	const stackUpshift = SPEECH_BUBBLE_ARROW_STACK_UPSHIFT_PX;
	const borderInnerExtraDown = SPEECH_BUBBLE_ARROW_BORDER_INNER_EXTRA_DOWN_PX;
	const containerWidthPx = outerHalfWidth * 2;
	const containerHeightPx = outerHeight;
	const stackContainerStyle = {
		width: containerWidthPx,
		height: containerHeightPx,
		transform: `translateY(${extraDownPx - stackUpshift + borderInnerExtraDown}px)`,
	} as const;

	if (direction === "up") {
		if (mount === "radial") {
			return null;
		}
		return (
			<span
				className="pointer-events-none absolute left-1/2 top-0 z-[2] flex -translate-x-1/2 -translate-y-full justify-center leading-none"
				aria-hidden
			>
				<span className="relative shrink-0" style={stackContainerStyle}>
					<span
						className={cn(
							"absolute left-1/2 bottom-0 z-[1] size-0 -translate-x-1/2 border-x-transparent",
							tokens.arrowUpOuterClassName,
						)}
						style={{
							borderLeftWidth: outerHalfWidth,
							borderRightWidth: outerHalfWidth,
							borderBottomWidth: outerHeight,
						}}
					/>
					<span
						className={cn(
							"absolute left-1/2 bottom-0 z-[2] size-0 -translate-x-1/2 border-x-transparent",
							tokens.arrowUpInnerClassName,
						)}
						style={{
							borderLeftWidth: innerHalfWidth,
							borderRightWidth: innerHalfWidth,
							borderBottomWidth: innerHeight,
						}}
					/>
				</span>
			</span>
		);
	}

	const arrowTrianglesDown = (
		<span className="relative shrink-0" style={stackContainerStyle}>
			<span
				className={cn(
					"absolute left-1/2 top-0 z-[1] size-0 -translate-x-1/2 border-x-transparent",
					tokens.arrowDownOuterClassName,
				)}
				style={{
					borderLeftWidth: outerHalfWidth,
					borderRightWidth: outerHalfWidth,
					borderTopWidth: outerHeight,
				}}
			/>
			<span
				className={cn(
					"absolute left-1/2 top-0 z-[2] size-0 -translate-x-1/2 border-x-transparent",
					tokens.arrowDownInnerClassName,
				)}
				style={{
					borderLeftWidth: innerHalfWidth,
					borderRightWidth: innerHalfWidth,
					borderTopWidth: innerHeight,
				}}
			/>
		</span>
	);

	if (mount === "radial") {
		return (
			<span
				className="pointer-events-none flex justify-center leading-none"
				aria-hidden
			>
				{arrowTrianglesDown}
			</span>
		);
	}

	return (
		<span
			className="pointer-events-none absolute bottom-0 left-1/2 z-[2] flex -translate-x-1/2 translate-y-[calc(100%-5px)] justify-center leading-none"
			aria-hidden
		>
			{arrowTrianglesDown}
		</span>
	);
}

function SpeechBubbleFace({
	children,
	imageSrc,
	imageAlt,
	sizePx,
	tokens,
	faceShape = "circle",
	density = "default",
	omitShell = false,
	glassFace = false,
}: {
	children?: ReactNode;
	imageSrc?: string;
	imageAlt?: string;
	sizePx: number;
	tokens: SpeechBubbleSurfaceTokens;
	faceShape?: SpeechBubbleFaceShape;
	density?: SpeechBubbleDensity;
	omitShell?: boolean;
	/** System glass face fill — radius comes from {@link GlassBorderPanel}. */
	glassFace?: boolean;
}) {
	const imagePx = resolvePortraitImagePx(sizePx);
	const isCompactText = density === "compact" && !imageSrc;
	const sizedFace =
		imageSrc != null || (glassFace && faceShape === "circle" && sizePx > 0);
	const face = (
		<div
			className={cn(
				omitShell
					? glassFace
						? null
						: cn(
								"bg-white",
								resolveSpeechBubbleFaceRadiusClass(faceShape),
							)
					: tokens.faceClassName,
				"relative z-[3] flex items-center justify-center",
				imageSrc
					? "p-2"
					: isCompactText
						? "px-1 py-0.5 text-center"
						: sizedFace && !imageSrc
							? "overflow-hidden p-0"
							: "px-4 py-2 text-center",
			)}
			style={
				sizedFace
					? ({
							minWidth: sizePx,
							minHeight: sizePx,
							width: sizePx,
							height: sizePx,
						} satisfies CSSProperties)
					: undefined
			}
		>
			{imageSrc ? (
				<div
					className="relative shrink-0"
					style={{
						width: imagePx,
						height: imagePx,
					}}
				>
					<Image
						src={imageSrc}
						alt={imageAlt ?? ""}
						fill
						className="object-contain"
						sizes={`${imagePx}px`}
						aria-hidden={!imageAlt}
					/>
				</div>
			) : (
				children
			)}
		</div>
	);

	if (
		omitShell ||
		!tokens.shellClassName ||
		(!imageSrc && faceShape === "circle")
	) {
		return glassFace ? (
			<GlassBorderPanel
				className={cn(
					"relative z-[3] min-h-0 bg-background",
					faceShape === "circle"
						? "overflow-hidden"
						: SPEECH_BUBBLE_SYSTEM_FACE_BORDER_CLASS,
				)}
			>
				{face}
			</GlassBorderPanel>
		) : (
			<div className="relative z-[1]">{face}</div>
		);
	}

	return (
		<div className={cn("relative z-[1]", tokens.shellClassName)}>
			{face}
		</div>
	);
}

/**
 * Glass path — shell tip behind GlassBorder, mid+fill above (BlockQuote parity).
 * `mount="radial"` skips edge anchoring so a parent can rotate the tip around the face.
 */
function SpeechBubbleGlassVerticalArrow({
	direction,
	geometry,
	layers,
	shellFill,
	borderFill,
	faceFill,
	mount = "edge",
}: {
	direction: SpeechBubbleArrowDirection;
	geometry: SpeechBubbleStackedArrowGeometry;
	layers: "shell" | "midFill";
	shellFill: string;
	borderFill: string;
	faceFill: string;
	mount?: SpeechBubbleArrowMount;
}) {
	const isUp = direction === "up";
	const isRadial = mount === "radial";
	const towardFace = isUp
		? `translateY(calc(-100% + ${geometry.stackUpshiftPx}px))`
		: `translateY(calc(100% - ${geometry.stackUpshiftPx}px))`;

	if (layers === "shell") {
		return (
			<span
				className={cn(
					"pointer-events-none z-0",
					isRadial
						? "relative flex justify-center leading-none"
						: "absolute left-1/2",
				)}
				style={
					isRadial
						? {
								width: geometry.shellHalfWidthPx * 2,
								height: geometry.shellHeightPx,
							}
						: {
								...(isUp ? { top: 0 } : { bottom: 0 }),
								width: geometry.shellHalfWidthPx * 2,
								height: geometry.shellHeightPx,
								transform: `${towardFace} translateX(-50%)`,
							}
				}
				aria-hidden
			>
				<span
					className="absolute left-1/2 size-0 -translate-x-1/2 border-x-transparent"
					style={{
						...(isUp ? { bottom: 0 } : { top: 0 }),
						borderLeftWidth: geometry.shellHalfWidthPx,
						borderRightWidth: geometry.shellHalfWidthPx,
						...(isUp
							? { borderBottomWidth: geometry.shellHeightPx }
							: { borderTopWidth: geometry.shellHeightPx }),
						...(isUp
							? { borderBottomColor: shellFill }
							: { borderTopColor: shellFill }),
					}}
				/>
			</span>
		);
	}

	const borderInnerShift = isUp
		? geometry.borderInnerExtraDownPx
		: -geometry.borderInnerExtraDownPx;

	return (
		<span
			className={cn(
				"pointer-events-none z-[2]",
				isRadial
					? "relative flex justify-center leading-none"
					: "absolute left-1/2",
			)}
			style={
				isRadial
					? {
							width: geometry.shellHalfWidthPx * 2,
							height: geometry.shellHeightPx,
						}
					: {
							...(isUp ? { top: 0 } : { bottom: 0 }),
							width: geometry.shellHalfWidthPx * 2,
							height: geometry.shellHeightPx,
							transform: `${towardFace} translateX(-50%)`,
						}
			}
			aria-hidden
		>
			<span
				className="absolute left-1/2"
				style={{
					width: geometry.outerHalfWidthPx * 2,
					height: geometry.outerHeightPx,
					transform: `translateX(-50%) translateY(${borderInnerShift}px)`,
					...(isUp ? { bottom: 0 } : { top: 0 }),
				}}
			>
				<span
					className="absolute left-1/2 size-0 -translate-x-1/2 border-x-transparent"
					style={{
						...(isUp ? { bottom: 0 } : { top: 0 }),
						borderLeftWidth: geometry.outerHalfWidthPx,
						borderRightWidth: geometry.outerHalfWidthPx,
						...(isUp
							? { borderBottomWidth: geometry.outerHeightPx }
							: { borderTopWidth: geometry.outerHeightPx }),
						...(isUp
							? { borderBottomColor: borderFill }
							: { borderTopColor: borderFill }),
					}}
				/>
				<span
					className="absolute left-1/2 z-[1] size-0 -translate-x-1/2 border-x-transparent"
					style={{
						...(isUp ? { bottom: 0 } : { top: 0 }),
						borderLeftWidth: geometry.innerHalfWidthPx,
						borderRightWidth: geometry.innerHalfWidthPx,
						...(isUp
							? { borderBottomWidth: geometry.innerHeightPx }
							: { borderTopWidth: geometry.innerHeightPx }),
						...(isUp
							? { borderBottomColor: faceFill }
							: { borderTopColor: faceFill }),
					}}
				/>
			</span>
		</span>
	);
}

const SPEECH_BUBBLE_ARROW_DOWN_ATAN2_DEG = 90;

/** Vertical center of a glass {@link SpeechBubble} face (shell + frosted band + face). */
export function resolveSpeechBubbleGlassChromeCenterTopPx(
	sizePx: number,
	glassBorderWidthPx: number,
): number {
	return GLASS_SHELL_BORDER_WIDTH_PX + glassBorderWidthPx + sizePx / 2;
}

/** Radial glass tip offset — nests stacked arrow into the frosted rim at the face edge. */
export function resolveSpeechBubbleGlassRadialArrowOffsetPx(
	sizePx: number,
	glassBorderWidthPx: number,
	geometry: SpeechBubbleStackedArrowGeometry,
): number {
	return (
		resolveSpeechBubbleGlassChromeCenterTopPx(sizePx, glassBorderWidthPx) -
		geometry.stackUpshiftPx
	);
}

interface SpeechBubbleRadialArrowPivotParams {
	sizePx: number;
	faceShape: SpeechBubbleFaceShape;
	arrowRotationDeg: number;
	arrowRotationTransition?: string;
	/** Legacy solid-shell portrait path (e.g. hex map finder). */
	showShellBorder?: boolean;
	/** GlassBorder inner path — includes frosted band in center + offset. */
	glassBorderWidthPx?: number;
	glassArrowGeometry?: SpeechBubbleStackedArrowGeometry;
}

export function resolveSpeechBubbleRadialArrowPivotStyle(
	params: SpeechBubbleRadialArrowPivotParams,
): CSSProperties {
	const {
		sizePx,
		faceShape,
		arrowRotationDeg,
		arrowRotationTransition,
		showShellBorder = false,
		glassBorderWidthPx,
		glassArrowGeometry,
	} = params;

	const centerTopPx =
		glassBorderWidthPx != null
			? resolveSpeechBubbleGlassChromeCenterTopPx(
					sizePx,
					glassBorderWidthPx,
				)
			: resolveSpeechBubbleFaceCenterTopPx(sizePx, showShellBorder);

	const offsetPx =
		glassBorderWidthPx != null && glassArrowGeometry
			? resolveSpeechBubbleGlassRadialArrowOffsetPx(
					sizePx,
					glassBorderWidthPx,
					glassArrowGeometry,
				)
			: resolveSpeechBubbleArrowOffsetFromFaceCenterPx(
					sizePx,
					faceShape,
					showShellBorder,
				);

	return {
		top: centerTopPx,
		transform: `translate(-50%, -50%) rotate(${arrowRotationDeg - SPEECH_BUBBLE_ARROW_DOWN_ATAN2_DEG}deg) translateY(${offsetPx}px)`,
		transition: arrowRotationTransition,
	};
}

function resolveGlassArrowFills(
	variant: SpeechBubbleVariant,
	overrides?: {
		arrowFill?: string;
		arrowBorderFill?: string;
		arrowShellFill?: string;
	},
): {
	shellFill: string;
	borderFill: string;
	faceFill: string;
} {
	if (variant === "dark") {
		return {
			shellFill: overrides?.arrowShellFill ?? "rgb(55 59 64)",
			borderFill: overrides?.arrowBorderFill ?? "rgb(55 59 64)",
			faceFill: overrides?.arrowFill ?? "#0f1320",
		};
	}
	return {
		shellFill:
			overrides?.arrowShellFill ?? SPEECH_BUBBLE_SYSTEM_ARROW_SHELL_FILL,
		borderFill:
			overrides?.arrowBorderFill ??
			SPEECH_BUBBLE_SYSTEM_ARROW_BORDER_FILL,
		faceFill: overrides?.arrowFill ?? SPEECH_BUBBLE_SYSTEM_ARROW_FACE_FILL,
	};
}

export function SpeechBubble({
	children,
	imageSrc,
	imageAlt,
	arrowDirection = "down",
	arrowRotationDeg,
	arrowRotationTransition,
	variant = "light",
	faceShape = "circle",
	density = "default",
	glassBorder: glassBorderProp,
	glassBorderWidth: glassBorderWidthProp,
	glassTone: glassToneProp,
	glassBorderProps,
	arrowFill,
	arrowBorderFill,
	arrowShellFill,
	arrowScale = 1,
	hideArrow = false,
	floatDurationSec = PULSE_FLOAT_DEFAULT_DURATION_S,
	floatDelaySec = 0,
	className,
	sizePx = SPEECH_BUBBLE_PORTRAIT_SIZE_PX,
	enableFloat = true,
}: SpeechBubbleProps) {
	const useGlassBorder =
		glassBorderProp ??
		(variant === "light" && imageSrc == null && arrowRotationDeg == null);

	const {
		borderWidth: glassBorderWidthFromProps,
		tone: glassToneFromProps,
		radius: glassRadiusFromProps,
		shellBorderClassName: glassShellBorderFromProps,
		plateClassName: glassPlateClassNameFromProps,
		largeBlur: glassLargeBlurFromProps,
		className: glassClassName,
		wrapperClassName: glassWrapperClassName,
		contentClassName: glassContentClassName,
		...glassBorderRest
	} = glassBorderProps ?? {};

	const glassBorderWidth =
		glassBorderWidthProp ??
		glassBorderWidthFromProps ??
		GLASS_BORDER_DEFAULT_BORDER_WIDTH_PX;
	const glassTone = glassToneProp ?? glassToneFromProps ?? "system";
	const glassRadius = glassRadiusFromProps ?? resolveGlassRadius(faceShape);
	const glassShellBorderClassName =
		glassShellBorderFromProps ?? SPEECH_BUBBLE_SYSTEM_SHELL_BORDER_CLASS;
	/** Opaque color-mix plate — matches arrow shell so tip/rim union over any backdrop. */
	const glassPlateClassName =
		glassPlateClassNameFromProps ?? SPEECH_BUBBLE_SYSTEM_GLASS_PLATE_CLASS;
	const glassLargeBlur = glassLargeBlurFromProps ?? false;

	const tokens = resolveSpeechBubbleTokens(variant, faceShape);
	const resolvedTokens =
		density === "compact" && tokens.shellClassName
			? {
					...tokens,
					shellClassName: tokens.shellClassName.replace(
						"p-[3px]",
						"p-0.5",
					),
				}
			: tokens;

	const glassGeometry = resolveSpeechBubbleStackedArrowGeometry({
		glassBorder: true,
		glassBorderWidthPx: glassBorderWidth,
		arrowScale,
	});
	const glassFills = resolveGlassArrowFills(variant, {
		arrowFill,
		arrowBorderFill,
		arrowShellFill,
	});

	if (useGlassBorder) {
		const glassRadialPivotStyle =
			arrowRotationDeg != null
				? resolveSpeechBubbleRadialArrowPivotStyle({
						sizePx,
						faceShape,
						arrowRotationDeg,
						arrowRotationTransition,
						glassBorderWidthPx: glassBorderWidth,
						glassArrowGeometry: glassGeometry,
					})
				: null;
		const glassArrowDirection =
			glassRadialPivotStyle != null ? "down" : arrowDirection;
		const bubble = (
			<div
				className={cn(
					"relative isolate w-max max-w-[calc(100vw-2rem)]",
					SPEECH_BUBBLE_SYSTEM_ARROW_CSS_VARS_CLASS,
					SPEECH_BUBBLE_FLOAT_DROP_SHADOW,
					className,
				)}
				data-speech-bubble-chrome="glass"
			>
				{/* Outer glass tip behind the frosted rim (BlockQuote / PA parity). */}
				{!hideArrow &&
					(glassRadialPivotStyle != null ? (
						<span
							className="pointer-events-none absolute left-1/2 z-0"
							style={glassRadialPivotStyle}
							aria-hidden
						>
							<span className="relative flex justify-center leading-none">
								<span className="absolute top-0 left-1/2 z-0 -translate-x-1/2">
									<SpeechBubbleGlassVerticalArrow
										direction={glassArrowDirection}
										geometry={glassGeometry}
										layers="shell"
										shellFill={glassFills.shellFill}
										borderFill={glassFills.borderFill}
										faceFill={glassFills.faceFill}
										mount="radial"
									/>
								</span>
							</span>
						</span>
					) : (
						<SpeechBubbleGlassVerticalArrow
							direction={glassArrowDirection}
							geometry={glassGeometry}
							layers="shell"
							shellFill={glassFills.shellFill}
							borderFill={glassFills.borderFill}
							faceFill={glassFills.faceFill}
						/>
					))}
				<GlassBorder
					variant="inner"
					tone={glassTone}
					borderWidth={glassBorderWidth}
					radius={glassRadius}
					shellBorderClassName={glassShellBorderClassName}
					plateClassName={glassPlateClassName}
					largeBlur={glassLargeBlur}
					className={cn(
						"relative z-[1] block w-max max-w-full",
						glassClassName,
					)}
					wrapperClassName={cn(
						"relative z-[1] block w-max max-w-full",
						glassWrapperClassName,
					)}
					contentClassName={cn(
						"min-h-0 w-max max-w-full",
						glassContentClassName,
					)}
					{...glassBorderRest}
				>
					<SpeechBubbleFace
						imageSrc={imageSrc}
						imageAlt={imageAlt}
						sizePx={sizePx}
						tokens={resolvedTokens}
						faceShape={faceShape}
						density={density}
						omitShell
						glassFace
					>
						{children}
					</SpeechBubbleFace>
				</GlassBorder>
				{/* Border + face tips above the frosted rim. */}
				{!hideArrow &&
					(glassRadialPivotStyle != null ? (
						<span
							className="pointer-events-none absolute left-1/2 z-[2]"
							style={glassRadialPivotStyle}
							aria-hidden
						>
							<span className="relative flex justify-center leading-none">
								<span className="absolute top-0 left-1/2 z-[2] -translate-x-1/2">
									<SpeechBubbleGlassVerticalArrow
										direction={glassArrowDirection}
										geometry={glassGeometry}
										layers="midFill"
										shellFill={glassFills.shellFill}
										borderFill={glassFills.borderFill}
										faceFill={glassFills.faceFill}
										mount="radial"
									/>
								</span>
							</span>
						</span>
					) : (
						<SpeechBubbleGlassVerticalArrow
							direction={glassArrowDirection}
							geometry={glassGeometry}
							layers="midFill"
							shellFill={glassFills.shellFill}
							borderFill={glassFills.borderFill}
							faceFill={glassFills.faceFill}
						/>
					))}
			</div>
		);

		if (!enableFloat) {
			return bubble;
		}
		return (
			<PulseFloatMotion
				durationSec={floatDurationSec}
				delaySec={floatDelaySec}
			>
				{bubble}
			</PulseFloatMotion>
		);
	}

	const arrowExtraDownPx = resolveSpeechBubbleArrowExtraDownPx(faceShape);
	const showShellBorder = Boolean(
		resolvedTokens.shellClassName &&
			(imageSrc != null || faceShape !== "circle"),
	);
	const radialPivotStyle =
		arrowRotationDeg != null
			? resolveSpeechBubbleRadialArrowPivotStyle({
					sizePx,
					faceShape,
					arrowRotationDeg,
					arrowRotationTransition,
					showShellBorder,
				})
			: null;
	const radialArrowShellBack =
		arrowRotationDeg != null && showShellBorder && radialPivotStyle ? (
			<span
				className="pointer-events-none absolute left-1/2 z-0"
				style={radialPivotStyle}
				aria-hidden
			>
				<span className="relative flex justify-center leading-none">
					<span className="absolute top-0 left-1/2 z-0 -translate-x-1/2">
						<SpeechBubbleShellArrow
							direction="down"
							tokens={resolvedTokens}
							extraDownPx={arrowExtraDownPx}
							mount="radial"
						/>
					</span>
				</span>
			</span>
		) : null;
	const radialArrowBorderFront =
		arrowRotationDeg != null && radialPivotStyle ? (
			<span
				className="pointer-events-none absolute left-1/2 z-[2]"
				style={radialPivotStyle}
				aria-hidden
			>
				<span className="relative flex justify-center leading-none">
					<span className="absolute top-0 left-1/2 z-[2] -translate-x-1/2">
						<SpeechBubbleArrow
							direction="down"
							tokens={resolvedTokens}
							extraDownPx={arrowExtraDownPx}
							mount="radial"
						/>
					</span>
				</span>
			</span>
		) : null;

	const bubble = (
		<div
			className={cn(
				"relative isolate w-max max-w-[calc(100vw-2rem)]",
				SPEECH_BUBBLE_FLOAT_DROP_SHADOW,
				className,
			)}
			data-speech-bubble-chrome="legacy"
		>
			{showShellBorder && arrowRotationDeg == null && !hideArrow ? (
				<SpeechBubbleShellArrow
					direction={arrowDirection}
					tokens={resolvedTokens}
					extraDownPx={arrowExtraDownPx}
				/>
			) : null}
			{hideArrow ? null : radialArrowShellBack}
			<SpeechBubbleFace
				imageSrc={imageSrc}
				imageAlt={imageAlt}
				sizePx={sizePx}
				tokens={resolvedTokens}
				faceShape={faceShape}
				density={density}
			>
				{children}
			</SpeechBubbleFace>
			{hideArrow ? null : arrowRotationDeg == null ? (
				<SpeechBubbleArrow
					direction={arrowDirection}
					tokens={resolvedTokens}
					extraDownPx={arrowExtraDownPx}
				/>
			) : (
				radialArrowBorderFront
			)}
		</div>
	);

	if (!enableFloat) {
		return bubble;
	}

	return (
		<PulseFloatMotion
			durationSec={floatDurationSec}
			delaySec={floatDelaySec}
		>
			{bubble}
		</PulseFloatMotion>
	);
}
